import { useEffect, useState } from 'react';
import { Server, Fan, Thermometer, Zap, AlertCircle } from 'lucide-react';

interface Enclosure {
  id: string;
  vendor: string;
  model: string;
  serial: string;
  bays: number;
  hba: string;
  expander: string;
  firmware: string;
  alarm: boolean;
  psus: { id: number; status: string }[];
  fans: { id: number; rpm: number; status: string }[];
  temperatureSensors: { id: number; label: string; tempC: number }[];
}

export default function Enclosures() {
  const [enclosures, setEnclosures] = useState<Enclosure[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/enclosures', { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => {
        setEnclosures(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500">Loading enclosures...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
        <Server size={24} /> JBOD Enclosures
      </h1>

      {enclosures.map((enc) => (
        <div key={enc.id} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="text-lg font-bold text-slate-900 dark:text-white">{enc.vendor} {enc.model}</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">Serial: {enc.serial} • Firmware: {enc.firmware} • Bays: {enc.bays}</div>
            </div>
            <div className="flex items-center gap-3">
              {enc.alarm && (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
                  <AlertCircle size={14} /> Alarm active
                </span>
              )}
              <span className="text-sm text-slate-500 dark:text-slate-400">{enc.hba} / {enc.expander}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SubCard title="Power Supplies" icon={<Zap size={16} />}>
              <div className="flex gap-2">
                {enc.psus.map((psu) => (
                  <span key={psu.id} className={`px-2 py-1 rounded text-xs font-semibold ${psu.status === 'ok' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300'}`}>
                    PSU {psu.id}: {psu.status}
                  </span>
                ))}
              </div>
            </SubCard>

            <SubCard title="Fans" icon={<Fan size={16} />}>
              <div className="flex flex-wrap gap-2">
                {enc.fans.map((fan) => (
                  <span key={fan.id} className="text-xs text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                    {fan.rpm} RPM
                  </span>
                ))}
              </div>
            </SubCard>

            <SubCard title="Temperature" icon={<Thermometer size={16} />}>
              <div className="space-y-1">
                {enc.temperatureSensors.map((s) => (
                  <div key={s.id} className="flex justify-between text-xs">
                    <span className="text-slate-500 dark:text-slate-400">{s.label}</span>
                    <span className="font-medium text-slate-900 dark:text-white">{s.tempC}°C</span>
                  </div>
                ))}
              </div>
            </SubCard>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">{icon} {title}</div>
      {children}
    </div>
  );
}
